import React from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  ListGroup,
  ListGroupItem,
} from "reactstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BreadCrumb from "Components/Common/BreadCrumb";
import { APP_NAME_SHORT } from "common/constants";
import { usePageActivity } from "Components/Hooks/UsePageActivity";
import AsyncSelect from "react-select/async";
import {
  getContractor,
  getContractors,
  mergeContractors,
  getContractorSummary,
} from "common/services/contractors.service";
import { normalize } from "common/lib/normalize";
import { toast } from "react-toastify";
import * as moment from "moment";

const tableCell = (label: string, value: React.ReactNode) => (
  <tr>
    <th style={{ width: 200 }}>{label}</th>
    <td>{value ?? "-"}</td>
  </tr>
);

const MergeContractor: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: sourceId } = useParams();

  document.title = `Merge Contractor | ${APP_NAME_SHORT}`;
  usePageActivity(location.pathname, "Merge Contractor", sourceId || null);

  const [loadingSource, setLoadingSource] = React.useState<boolean>(true);
  const [loadingTarget, setLoadingTarget] = React.useState<boolean>(false);
  const [source, setSource] = React.useState<any>(null);
  const [target, setTarget] = React.useState<any>(null);
  const [targetOption, setTargetOption] = React.useState<any>(null);
  const [confirmOpen, setConfirmOpen] = React.useState<boolean>(false);
  const [merging, setMerging] = React.useState<boolean>(false);
  const [sourceSummary, setSourceSummary] = React.useState<any>(null);
  const [targetSummary, setTargetSummary] = React.useState<any>(null);

  const loadSource = React.useCallback(async () => {
    if (!sourceId) return;
    try {
      setLoadingSource(true);
      const response: any = await getContractor(sourceId, {
        fields: [
          "contractorName",
          "acronym",
          "createdAt",
          "updatedAt",
          "website",
        ],
        populate: {
          organizationType: { fields: ["name"] },
          contracts: { count: true },
          performanceRatings: {
            populate: { performanceRatingType: true, ratedBy: true },
          },
          semgMentions: true,
          boardOfDirectors: { populate: { person: true } },
          contactPersons: true,
          contractorRegistration: true,
          phoneNumber: true,
          emailAddress: true,
          physicalAddress: true,
          postalAddress: true,
        },
      });
      setSource(normalize(response.data));
      try {
        const summary = await getContractorSummary({ id: sourceId });
        setSourceSummary(summary);
      } catch (e) {
        // ignore
      }
    } catch (e: any) {
      toast.error("Failed to load source contractor");
    } finally {
      setLoadingSource(false);
    }
  }, [sourceId]);

  React.useEffect(() => {
    loadSource();
  }, [loadSource]);

  const loadOptions = React.useCallback(
    async (inputValue: string) => {
      try {
        const response: any = await getContractors({
          filters: {
            $and: [
              {
                contractorName: {
                  $containsi: inputValue || "",
                },
              },
              sourceId
                ? {
                    id: {
                      $ne: sourceId,
                    },
                  }
                : {},
            ],
          },
          pagination: { pageSize: 20, page: 1 },
          sort: ["contractorName:asc"],
          fields: ["contractorName"],
        });
        const list = normalize(response.data) || [];
        return list.map((c: any) => ({ value: c.id, label: c.contractorName }));
      } catch (e: any) {
        return [];
      }
    },
    [sourceId]
  );

  const handleSelectTarget = async (option: any) => {
    if (!option) {
      setTarget(null);
      setTargetOption(null);
      return;
    }
    try {
      setLoadingTarget(true);
      const response: any = await getContractor(option.value, {
        fields: [
          "contractorName",
          "acronym",
          "createdAt",
          "updatedAt",
          "website",
        ],
        populate: {
          organizationType: { fields: ["name"] },
          contracts: { count: true },
          performanceRatings: {
            populate: { performanceRatingType: true, ratedBy: true },
          },
          semgMentions: true,
          boardOfDirectors: { populate: { person: true } },
          contactPersons: true,
          contractorRegistration: true,
          phoneNumber: true,
          emailAddress: true,
          physicalAddress: true,
          postalAddress: true,
        },
      });
      const normalized = normalize(response.data);
      setTarget(normalized);
      // ensure the select shows the contractor name when prefilled from URL
      setTargetOption({
        value: option.value,
        label:
          normalized?.contractorName || option?.label || String(option.value),
      });
      try {
        const summary = await getContractorSummary({ id: option.value });
        setTargetSummary(summary);
      } catch (e) {
        // ignore
      }
    } catch (e: any) {
      toast.error("Failed to load target contractor");
    } finally {
      setLoadingTarget(false);
    }
  };

  // Read target from query string (?target=ID) and load it on mount/change
  const targetIdFromQuery = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("target");
  }, [location.search]);

  React.useEffect(() => {
    if (
      targetIdFromQuery &&
      (!targetOption ||
        String(targetOption?.value) !== String(targetIdFromQuery))
    ) {
      handleSelectTarget({
        value: targetIdFromQuery,
        label: String(targetIdFromQuery),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetIdFromQuery]);

  const renderSEMGMentions = (record: any, extraClass?: string) => {
    const items = record?.semgMentions || [];
    return (
      <Card className={`mt-3 ${extraClass || ""}`}>
        <CardHeader className="d-flex align-items-center">
          <div className="text-muted flex-grow-1 d-flex align-items-center">
            <h6 className="mb-0 fw-semibold text-uppercase">
              SEMG Mentions
              <small className="badge bg-primary-subtle align-bottom ms-1 text-primary">
                {items?.length || 0}
              </small>
            </h6>
          </div>
        </CardHeader>
        <CardBody className="card-body">
          {items?.length === 0 && (
            <div className="text-muted">No SEMG mentions</div>
          )}
          {items?.map((item: any, index: number) => (
            <div
              className="table-responsive border border-dashed rounded p-2 mb-3"
              key={index}
            >
              <table className="table table-sm table-borderless mb-0">
                <tbody>
                  <tr>
                    <td
                      className="fw-medium"
                      style={{ whiteSpace: "nowrap", width: "30%" }}
                    >
                      SEMG Report No:
                    </td>
                    <td>{item?.reportNumber || ""}</td>
                  </tr>
                  <tr>
                    <td className="fw-medium">SEMG Page:</td>
                    <td>{item?.SEMGPage || ""}</td>
                  </tr>
                  <tr>
                    <td className="fw-medium">SEMG Paragraph:</td>
                    <td>{item?.SEMGParagraph || ""}</td>
                  </tr>
                  <tr>
                    <td className="fw-medium">SEMG Section:</td>
                    <td>{item?.SEMGSection || ""}</td>
                  </tr>
                  <tr>
                    <td className="fw-medium">SEMG Commentary:</td>
                    <td>{item?.SEMGCommentary || ""}</td>
                  </tr>
                  <tr>
                    <td className="fw-medium">SEMG Location/Region:</td>
                    <td>{item?.SEMGRegion || ""}</td>
                  </tr>
                  <tr>
                    <td className="fw-medium">SEMG Last Updated:</td>
                    <td>
                      {item?.updatedAt
                        ? moment(item?.updatedAt).format("DD MMM, YYYY")
                        : ""}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))}
        </CardBody>
      </Card>
    );
  };

  const renderBoardOfDirectors = (record: any, extraClass?: string) => {
    const list = record?.boardOfDirectors || [];
    return (
      <Card className={`mt-3 ${extraClass || ""}`}>
        <CardHeader className="d-flex align-items-center">
          <div className="text-muted flex-grow-1 d-flex align-items-center">
            <h6 className="mb-0 fw-semibold text-uppercase">
              Staff / Board of Directors
              <small className="badge bg-primary-subtle align-bottom ms-1 text-primary">
                {list?.length || 0}
              </small>
            </h6>
          </div>
        </CardHeader>
        <CardBody className="card-body">
          {list?.length === 0 && <div className="text-muted">No records</div>}
          {list?.length > 0 && (
            <ListGroup flush>
              {list.map((director: any, index: number) => (
                <ListGroupItem
                  key={index}
                  className="list-group-item-action"
                  onClick={() => {
                    if (director?.person?.id)
                      navigate(`/persons/${director?.person?.id}`);
                  }}
                  style={{
                    cursor: director?.person?.id ? "pointer" : "default",
                  }}
                >
                  <div className="float-end">
                    <div>
                      <i className="ri-time-line align-bottom me-1"></i>{" "}
                      {director?.updatedAt
                        ? moment(new Date(director?.updatedAt)).format(
                            "DD MMM, Y"
                          )
                        : ""}
                    </div>
                  </div>
                  <div className="d-flex mb-2 align-items-center">
                    <div className="flex-shrink-0">
                      <div className="avatar-sm  rounded-circle">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-24">
                          <i className="ri-group-line text-primary"></i>
                        </div>
                      </div>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <h5 className="list-title fs-15 mb-1">
                        {director?.person?.fullName || ""}
                      </h5>
                      <p className="list-text mb-0 fs-12">
                        {director?.role || ""}
                        <span className="ms-2">
                          {director?.fromDate
                            ? `( ${moment(new Date(director?.fromDate)).format(
                                "MMM, Y"
                              )} `
                            : ""}
                          {director?.toDate
                            ? `to ${moment(new Date(director?.toDate)).format(
                                "MMM, Y"
                              )} )`
                            : `${
                                director?.fromDate ? "to Current )" : ""
                              }`}{" "}
                        </span>
                      </p>
                    </div>
                  </div>
                </ListGroupItem>
              ))}
            </ListGroup>
          )}
        </CardBody>
      </Card>
    );
  };

  const onConfirmMerge = async () => {
    if (!sourceId || !targetOption?.value) return;
    try {
      setMerging(true);
      await mergeContractors({
        id: String(sourceId),
        contractorId: String(targetOption.value),
      });
      toast.success("Contractors merged successfully");
      setConfirmOpen(false);
      navigate(`/contractors/${sourceId}`);
    } catch (e: any) {
      toast.error("Failed to merge contractors");
    } finally {
      setMerging(false);
    }
  };

  const renderDetailsTable = (record: any) => {
    if (!record) return <div className="text-muted">No data</div>;
    return (
      <div className="table-responsive">
        <table className="table table-striped">
          <tbody>
            {tableCell("Name", record?.contractorName)}
            {tableCell("Acronym", record?.acronym)}
            {tableCell("Organization Type", record?.organizationType?.name)}
            {tableCell(
              "Created",
              record?.createdAt
                ? moment(record.createdAt).format("DD MMM YYYY")
                : "-"
            )}
            {tableCell(
              "Last Updated",
              record?.updatedAt
                ? moment(record.updatedAt).format("DD MMM YYYY")
                : "-"
            )}
            {tableCell("Website", record?.website || "-")}
          </tbody>
        </table>
      </div>
    );
  };

  const renderStatWidgets = (
    summary: any,
    record: any,
    extraClass?: string
  ) => {
    if (!summary && !record) return null;
    const totalValue = summary?.totalValueOfContracts ?? 0;
    const totalAssessments = summary?.totalAssessments ?? 0;
    const totalAgencies = summary?.totalAgencies ?? 0;
    const totalContracts =
      record?.contracts?.count ?? summary?.totalContracts ?? 0;

    const Widget = ({ title, value, prefix = "", suffix = "" }: any) => (
      <Card className={`card-animate ${extraClass || ""}`}>
        <CardBody>
          <div className="d-flex align-items-center">
            <div className="flex-grow-1 overflow-hidden">
              <p className="text-uppercase fw-medium text-muted text-truncate mb-0">
                {title}
              </p>
            </div>
          </div>
          <div className="d-flex align-items-end justify-content-between mt-3">
            <div>
              <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                {prefix}
                {typeof value === "number" ? value.toLocaleString() : value}
                {suffix}
              </h4>
            </div>
          </div>
        </CardBody>
      </Card>
    );

    return (
      <>
        <Row className="g-3 mb-0">
          <Col md={6} xl={6}>
            <Widget
              title="Total Contract Value"
              value={totalValue}
              prefix="$"
            />
          </Col>
          <Col md={6} xl={6}>
            <Widget title="Assessments" value={totalAssessments} />
          </Col>
        </Row>
        <Row className="g-3 mb-0">
          <Col md={6} xl={6}>
            <Widget title="UN Agencies" value={totalAgencies} />
          </Col>
          <Col md={6} xl={6}>
            <Widget title="Contracts" value={totalContracts} />
          </Col>
        </Row>
      </>
    );
  };

  const renderContactDetails = (record: any, extraClass?: string) => {
    if (!record) return null;
    return (
      <Card className={`mt-3 ${extraClass || ""}`}>
        <CardHeader className="d-flex align-items-center">
          <div className="text-muted flex-grow-1 d-flex align-items-center">
            <h6 className="mb-0 fw-semibold text-uppercase">Contact Details</h6>
          </div>
        </CardHeader>
        <CardBody className="card-body px-0 pt-0">
          <div className="px-3 pt-3">
            <Row>
              <Col sm={6}>
                <p className="mb-1">
                  <span className="fw-semibold text-body">Phone: </span>
                  {(record?.phoneNumber &&
                    record?.phoneNumber
                      .map((el: any) => el.name || "")
                      .join(", ")) ||
                    "Not Provided"}
                </p>
                <p className="mb-1">
                  <span className="fw-semibold text-body">Email: </span>
                  {(record?.emailAddress &&
                    record?.emailAddress
                      .map((el: any) => el.name || "")
                      .join(", ")) ||
                    "Not Provided"}
                </p>
                <p className="mb-1">
                  <span className="fw-semibold text-body">
                    Physical Address:{" "}
                  </span>
                  {(record?.physicalAddress &&
                    record?.physicalAddress
                      .map(
                        (el: any) =>
                          `${el.address || ""} ${el.city || ""} ${
                            el.country || ""
                          }`
                      )
                      .join(", ")) ||
                    "Not Provided"}
                </p>
              </Col>
              <Col sm={6}>
                <p className="mb-1">
                  <span className="fw-semibold text-body">
                    Postal Address:{" "}
                  </span>
                  {(record?.postalAddress &&
                    record?.postalAddress
                      .map(
                        (el: any) =>
                          `${el.address || ""} ${el.city || ""} ${
                            el.country || ""
                          }`
                      )
                      .join(", ")) ||
                    "Not Provided"}
                </p>
                <p className="mb-1">
                  <span className="fw-semibold text-body">
                    Business Website:{" "}
                  </span>
                  {record?.website || "Not Provided"}
                </p>
              </Col>
            </Row>
          </div>
        </CardBody>
      </Card>
    );
  };

  const renderContractorRegistration = (record: any, extraClass?: string) => {
    if (
      !record?.contractorRegistration ||
      record?.contractorRegistration.length === 0
    )
      return null;
    return (
      <Card className={`mt-3 ${extraClass || ""}`}>
        <CardHeader className="d-flex align-items-center border-bottom-dashed">
          <div className="text-muted flex-grow-1 d-flex align-items-center">
            <h6 className="mb-0 fw-semibold text-uppercase">
              Contractor Registration
            </h6>
          </div>
        </CardHeader>
        <CardBody>
          <div className="table-responsive table-card">
            <table className="table table-striped align-middle table-nowrap mb-0">
              <thead>
                <tr>
                  <th>Registration Name</th>
                  <th>Registration Type</th>
                  <th>Number</th>
                  <th>Date Registered</th>
                  <th>Length of Validity</th>
                  <th>Country</th>
                </tr>
              </thead>
              <tbody>
                {record?.contractorRegistration.map((el: any, idx: number) => (
                  <tr key={idx}>
                    <td>{el.registrationName || "Not Provided"}</td>
                    <td>{el.registrationType || "Not Provided"}</td>
                    <td>{el.registrationNumber || "Not Provided"}</td>
                    <td>
                      {el.registrationDate
                        ? moment(el.registrationDate).format("DD MMM, Y")
                        : "Not Provided"}
                    </td>
                    <td>{el.lengthOfValidity || "-"}</td>
                    <td>{el.countryOfRegistration || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    );
  };

  const renderPerformanceRatings = (record: any, extraClass?: string) => {
    const ratings = record?.performanceRatings || [];
    if (ratings.length === 0) return null;
    const countBy = (name: string) =>
      ratings.filter((r: any) => r?.performanceRatingType?.name === name)
        .length;
    return (
      <Card className={`mt-3 ${extraClass || ""}`}>
        <CardHeader className="d-flex align-items-center border-bottom-dashed">
          <div className="text-muted flex-grow-1">
            <h6 className="mb-0 fw-semibold text-uppercase">
              Performance Rating
            </h6>
          </div>
        </CardHeader>
        <CardBody>
          <Row className="gy-2">
            <Col md={12}>
              <div className="bg-light px-3 py-2 rounded-2 mb-2">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <div className="fs-16 align-middle text-success">
                      <i className="ri-star-fill"></i>{" "}
                      <i className="ri-star-fill"></i>{" "}
                      <i className="ri-star-fill"></i>{" "}
                      <i className="ri-star-fill"></i>{" "}
                      <i className="ri-star-half-fill"></i>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <h6 className="mb-0">
                      {countBy("Satisfactory")} out of {ratings.length}
                    </h6>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          <Row className="gy-2">
            <Col md={4}>
              <span className="fw-semibold">Not Satisfactory:</span>{" "}
              {countBy("Not Satisfactory")}
            </Col>
            <Col md={4}>
              <span className="fw-semibold">Suspended:</span>{" "}
              {countBy("Suspended")}
            </Col>
            <Col md={4}>
              <span className="fw-semibold">Globally Suspended:</span>{" "}
              {countBy("Globally Suspended")}
            </Col>
          </Row>
        </CardBody>
      </Card>
    );
  };

  const mergeDisabled =
    !targetOption?.value || merging || loadingSource || loadingTarget;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Merge Contractors"
            pageTitles={[
              { title: "Dashboard", link: "/" },
              { title: "Contractors", link: "/contractors" },
            ]}
            back
          />

          <Card className="mb-3">
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={8}>
                  <h5 className="card-title mb-0">
                    Select contractor to merge INTO source
                  </h5>
                </Col>
                <Col md={4}>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    isClearable
                    loadOptions={loadOptions}
                    onChange={handleSelectTarget}
                    value={targetOption}
                    placeholder="Search contractors to merge..."
                  />
                </Col>
              </Row>
            </CardHeader>
          </Card>

          <Row className="g-4">
            <Col lg={6}>
              <Card>
                <CardHeader>
                  <h6 className="card-title mb-0">Source Contractor</h6>
                </CardHeader>
                <CardBody>
                  {loadingSource ? (
                    <Spinner size="sm" />
                  ) : (
                    renderDetailsTable(source)
                  )}
                </CardBody>
              </Card>
              {renderStatWidgets(sourceSummary, source)}
              {renderContactDetails(source)}
              {renderContractorRegistration(source)}
              {renderPerformanceRatings(source)}
              {renderSEMGMentions(source)}
              {renderBoardOfDirectors(source)}
            </Col>
            <Col lg={6}>
              <Card className="card-light">
                <CardHeader>
                  <h6 className="card-title mb-0">
                    Target Contractor (to be merged)
                  </h6>
                </CardHeader>
                <CardBody>
                  {loadingTarget ? (
                    <Spinner size="sm" />
                  ) : (
                    renderDetailsTable(target)
                  )}
                </CardBody>
              </Card>
              {renderStatWidgets(targetSummary, target, "card-light")}
              {renderContactDetails(target, "card-light")}
              {renderContractorRegistration(target, "card-light")}
              {renderPerformanceRatings(target, "card-light")}
              {renderSEMGMentions(target, "card-light")}
              {renderBoardOfDirectors(target, "card-light")}
            </Col>
          </Row>

          <Card className="mt-4">
            <CardBody>
              <div className="d-flex justify-content-end">
                <Button
                  color="primary"
                  className="material-shadow-none"
                  disabled={mergeDisabled}
                  onClick={() => setConfirmOpen(true)}
                >
                  {merging ? <Spinner size="sm" className="me-2" /> : null}
                  Merge Contractors
                </Button>
              </div>
            </CardBody>
          </Card>

          <Modal
            isOpen={confirmOpen}
            toggle={() => setConfirmOpen((s) => !s)}
            centered
          >
            <ModalHeader toggle={() => setConfirmOpen((s) => !s)}>
              Confirm Merge
            </ModalHeader>
            <ModalBody>
              <Alert color="warning" className="material-shadow">
                This action cannot be undone once it has been completed.
              </Alert>
              <p className="mb-0">
                Are you sure you want to merge the target contractor into the
                source contractor?
              </p>
            </ModalBody>
            <ModalFooter>
              <Button
                color="light"
                className="material-shadow-none"
                onClick={() => setConfirmOpen(false)}
                disabled={merging}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                className="material-shadow-none"
                onClick={onConfirmMerge}
                disabled={merging}
              >
                {merging ? <Spinner size="sm" className="me-2" /> : null}
                Confirm Merge
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default MergeContractor;
